import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { getComplaints, createComplaint, rewriteComplaint } from '@/services/api';
import { Plus, AlertCircle, Clock, CheckCircle2, Sparkles, Loader2, Paperclip } from 'lucide-react';
import { toast } from 'sonner';

const ResidentComplaints = () => {
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isRewriting, setIsRewriting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'maintenance',
    priority: 'medium'
  });

  useEffect(() => {
    fetchComplaints();
  }, []);

  const fetchComplaints = async () => {
    try {
      const data = await getComplaints();
      setComplaints(data);
    } catch (error) {
      toast.error('Failed to load complaints');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await createComplaint(formData);
      toast.success('Complaint submitted successfully!');
      setDialogOpen(false);
      setFormData({ title: '', description: '', category: 'maintenance', priority: 'medium' });
      fetchComplaints();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit complaint');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAIRewrite = async () => {
    if (!formData.description.trim()) {
      toast.error('Please enter a description first');
      return;
    }
    
    setIsRewriting(true);
    try {
      const result = await rewriteComplaint(formData.title, formData.description, formData.category);
      setFormData({ ...formData, description: result.rewritten_description });
      toast.success('Complaint rewritten by AI!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to rewrite complaint. AI may not be configured.');
    } finally {
      setIsRewriting(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      open: { icon: AlertCircle, variant: 'destructive', label: 'Open' },
      in_progress: { icon: Clock, variant: 'secondary', label: 'In Progress' },
      resolved: { icon: CheckCircle2, variant: 'default', label: 'Resolved' },
      closed: { icon: CheckCircle2, variant: 'outline', label: 'Closed' }
    };
    const item = config[status] || config.open;
    const Icon = item.icon;
    return (
      <Badge variant={item.variant}>
        <Icon className="h-3 w-3 mr-1" />
        {item.label}
      </Badge>
    );
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="resident-complaints">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>Complaints</h1>
          <p className="text-slate-600">Manage your complaints and requests</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-secondary hover:bg-secondary/90" data-testid="new-complaint-btn">
              <Plus className="h-4 w-4 mr-2" />
              New Complaint
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Submit New Complaint</DialogTitle>
              <DialogDescription>Describe your issue and attach supporting documents</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  placeholder="Brief description of the issue"
                  maxLength={200}
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  data-testid="complaint-title-input"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="description">Description</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAIRewrite}
                    disabled={isRewriting || !formData.description.trim()}
                    className="text-xs h-7 gap-1 text-secondary border-secondary hover:bg-secondary/10"
                    data-testid="ai-rewrite-btn"
                  >
                    {isRewriting ? (
                      <>
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Rewriting...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-3 w-3" />
                        AI Rewrite
                      </>
                    )}
                  </Button>
                </div>
                <Textarea
                  id="description"
                  placeholder="Describe your issue in detail..."
                  maxLength={2000}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  data-testid="complaint-description-input"
                  rows={4}
                />
                <p className="text-xs text-slate-500">Tip: Write naturally, then click AI Rewrite to make it professional.</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                    <SelectTrigger data-testid="complaint-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="maintenance">Maintenance</SelectItem>
                      <SelectItem value="security">Security</SelectItem>
                      <SelectItem value="noise">Noise</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select value={formData.priority} onValueChange={(value) => setFormData({ ...formData, priority: value })}>
                    <SelectTrigger data-testid="complaint-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-secondary hover:bg-secondary/90" 
                data-testid="complaint-submit-btn"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Submit Complaint
                  </>
                )}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4">
        {complaints.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="p-12 text-center">
              <p className="text-slate-500">No complaints yet</p>
            </CardContent>
          </Card>
        ) : (
          complaints.map((complaint) => (
            <Card key={complaint.id} className="border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-testid="complaint-item">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-1">{complaint.title}</h3>
                    <p className="text-sm text-slate-500">Category: {complaint.category} | Priority: {complaint.priority}</p>
                  </div>
                  {getStatusBadge(complaint.status)}
                </div>
                <p className="text-slate-600 mb-4">{complaint.description}</p>
                {complaint.attachments && complaint.attachments.length > 0 && (
                  <div className="flex items-center gap-2 mb-3">
                    <Paperclip className="h-4 w-4 text-slate-400" />
                    <span className="text-sm text-slate-500">{complaint.attachments.length} attachment(s)</span>
                    {complaint.attachments.map((filename, idx) => (
                      <a
                        key={idx}
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/${filename}?auth=${localStorage.getItem('token')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-secondary hover:underline"
                      >
                        View
                      </a>
                    ))}
                  </div>
                )}
                <div className="text-xs text-slate-500">
                  Submitted: {new Date(complaint.created_at).toLocaleDateString()}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default ResidentComplaints;
